import moment from "moment";
import { AdjustmentValidation } from "../../../MealTrack/MTAPITypes";
import { DefaultCartText, DefaultDaySectionExpansionType } from "./orderSummary-Types";
export const NO_SUBTOTAL_FOUND = `Next order's subtotal not found.`;
export const NO_TOTAL_FOUND = `Next order's total not found.`;
/**
 * Gets the next order subtotal display value.
 * @param balance
 * @param settings
 * @param perMealCouponDiscountAppliedValue
 * @param useCurrencyLocalizer
 * @returns
 */
export const getNextOrderSubtotalDisplay = (balance, settings, perMealCouponDiscountAppliedValue, useCurrencyLocalizer) => {
    var _a;
    const nextOrderSubtotal = (_a = balance === null || balance === void 0 ? void 0 : balance.cost.subscription) === null || _a === void 0 ? void 0 : _a.subtotal;
    const subtotalWithDiscount = (nextOrderSubtotal !== null && nextOrderSubtotal !== void 0 ? nextOrderSubtotal : 0) - ((settings === null || settings === void 0 ? void 0 : settings.discountPerMeal.display) ? perMealCouponDiscountAppliedValue : 0);
    const { localizedCurrencyText: nextOrderSubtotalCurrencyText } = useCurrencyLocalizer(subtotalWithDiscount);
    return {
        nextOrderSubtotal,
        nextOrderSubtotalCurrencyText: nextOrderSubtotal ? nextOrderSubtotalCurrencyText : NO_SUBTOTAL_FOUND,
    };
};
/**
 * * Gets the total cost for a given day from the balance object.
 * @param day
 * @param balance
 * @param settings
 * @returns
 */
export const getDayPrice = (day, balance, settings) => {
    var _a, _b, _c;
    const mealDate = moment(day.date);
    const distributions = (_b = (_a = balance === null || balance === void 0 ? void 0 : balance.cost.order) === null || _a === void 0 ? void 0 : _a.distributions) !== null && _b !== void 0 ? _b : [];
    // Find the distribution that contains the day group for this date
    for (const dist of distributions) {
        const dayGroup = (_c = dist.dayGroups) === null || _c === void 0 ? void 0 : _c.find((g) => moment(g.day).isSame(mealDate, "day"));
        if (dayGroup) {
            const price = settings.discountPerMeal.display ? dayGroup.discountedCost : dayGroup.totalCost;
            return { dayPrice: price };
        }
    }
    return { dayPrice: 0 };
};
/**
 * * Gets the total cost for a given meal from the balance object.
 * @param mealDate
 * @param programMealId
 * @param balance
 * @returns
 */
export const getMealPrice = (mealDate, programMealId, balance) => {
    const dayDistribution = balance === null || balance === void 0 ? void 0 : balance.cost.order.distributions.find((d) => moment(d.day).isSame(mealDate, "day"));
    const dayGroup = dayDistribution === null || dayDistribution === void 0 ? void 0 : dayDistribution.dayGroups.find((d) => moment(d.day).isSame(mealDate, "day"));
    const mealFromBalance = dayGroup === null || dayGroup === void 0 ? void 0 : dayGroup.meals.find((m) => m.programMealId === programMealId);
    return mealFromBalance === null || mealFromBalance === void 0 ? void 0 : mealFromBalance.totalCost;
};
/**
 * Compares the meals to the subscription template to check if they match in terms of days and quantities
 *
 * @param meals
 * @param subscriptionMeals
 * @returns
 */
const compareMealsAndTemplate = (meals, subscriptionMeals) => {
    // Create maps to store quantities of meals and subscription meals by their respective days of the week
    const mealDays = new Map();
    const subscriptionMealDays = new Map();
    meals.forEach((meal) => {
        const dayOfWeek = moment(meal.MealDate).day();
        mealDays.set(dayOfWeek, meal.Quantity);
    });
    subscriptionMeals.forEach((cartSubscriptionMeal) => {
        subscriptionMealDays.set(cartSubscriptionMeal.DayOfWeek, cartSubscriptionMeal.Qty);
    });
    // If the number of unique days in meals and subscription meals are different, they don't match
    if (mealDays.size !== subscriptionMealDays.size) {
        return false;
    }
    // Check if each day in mealDays map has the same quantity as in subscriptionMealDays map
    for (const [day, quantity] of mealDays) {
        if (subscriptionMealDays.get(day) !== quantity) {
            return false;
        }
    }
    return true;
};
/**
 * Checks if the order contains a short week by comparing the meals to the subscription template
 *
 * @param meals
 * @param subscriptionMeals
 * @returns
 */
export const doesOrderContainShortWeek = (meals, subscriptionMeals) => {
    const mealsByWeek = groupMealsByWeek(meals);
    const firstWeekOfMeals = getFirstWeekOfMeals(mealsByWeek);
    if (!(firstWeekOfMeals === null || firstWeekOfMeals === void 0 ? void 0 : firstWeekOfMeals.meals))
        return false;
    // The template represents meals for a single week, so we only need to compare the first week's meals
    // to the template. Comparing all meals in the cart would be incorrect because they are repeated
    // across the subscription period.
    const doMealsMatchTemplate = compareMealsAndTemplate(firstWeekOfMeals.meals, subscriptionMeals);
    // If the meals do not match the subscription template, it indicates a short week
    return !doMealsMatchTemplate;
};
/**
 * Groups an array of meals by the starting date of the week in which each meal occurs.
 *
 * @param {Meal[]} meals - The array of meals to be grouped by week.
 * @returns {Record<string, Meal[]>} - An object where each key is the starting date of a week (in "YYYY-MM-DD" format)
 *                                     and the corresponding value is an array of meals that occur in that week.
 */
const groupMealsByWeek = (meals) => {
    return meals.reduce((acc, meal) => {
        const weekStart = moment(meal.MealDate).startOf("week").format("YYYY-MM-DD");
        if (!acc[weekStart]) {
            acc[weekStart] = [];
        }
        acc[weekStart].push(meal);
        return acc;
    }, {});
};
/**
 * Retrieves the first week of meals from a grouped meals-by-week object.
 *
 * @param {Record<string, Meal[]>} mealsByWeek - An object where each key is the starting date of a week
 *                                               and the corresponding value is an array of meals for that week.
 * @returns {{ weekStartDate: string; meals: Meal[] } | null} - An object containing the start date of the first week
 *                                                              and the meals for that week, or null if there are no meals.
 */
const getFirstWeekOfMeals = (mealsByWeek) => {
    const firstWeekEntry = Object.entries(mealsByWeek)[0];
    if (firstWeekEntry) {
        const [weekStartDate, meals] = firstWeekEntry;
        return { weekStartDate, meals };
    }
    return null; // Return null if mealsByWeek is empty or has no entries
};
/**
 * This function filters adjustments based on their validity and formats them for use in the order summary.
 *
 * @param {Balance | undefined} balance - The balance object
 * @returns {OrderSummaryDiscount[] | undefined} - An array of valid adjustments formatted for the order summary,
 * or `undefined` if no valid adjustments are found.
 */
export const getOrderAdjustments = (balance) => {
    return balance === null || balance === void 0 ? void 0 : balance.cost.order.adjustments.filter((adjustment) => adjustment.validation === AdjustmentValidation.Valid).map((adjustment) => {
        return {
            name: adjustment.name,
            type: adjustment.application,
            value: adjustment.adjustmentValue,
            appliedValue: adjustment.appliedValue,
            adjustmentType: adjustment.adjustmentType,
            validationMsg: adjustment.validation,
            automaticDiscount: adjustment.automatic,
        };
    });
};
/**
 * Convert the JSON object to CartVisibilitySettings for use in Order Summary.
 *
 * @param cartSettings - Cart Settings Configuration Model
 * @param hidePrices - SkipPayment Resource Lookup
 * @returns
 */
export const convertToCartVisibilitySettings = (cartSettings, hidePrices, showNoBalanceDueMessage = false) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x;
    return {
        planName: {
            display: cartSettings.DisplayPlanName,
            label: (_a = cartSettings.PlanNameDisplayText) !== null && _a !== void 0 ? _a : DefaultCartText.PLAN_NAME,
        },
        startDate: {
            display: cartSettings.DisplayStartDate,
            label: (_b = cartSettings.StartDateDisplayText) !== null && _b !== void 0 ? _b : DefaultCartText.START_DATE,
        },
        renewalPeriod: {
            display: cartSettings.DisplayRenewalPeriod,
            label: DefaultCartText.SUBSCRIPTION_PERIOD,
        },
        omissions: {
            display: cartSettings.DisplayOmissions,
            label: (_c = cartSettings.OmissionsDisplayText) !== null && _c !== void 0 ? _c : DefaultCartText.OMISSIONS,
        },
        deliveryMethod: {
            display: cartSettings.DisplayDeliveryMethod,
            label: (_d = cartSettings.DeliveryMethodDisplayText) !== null && _d !== void 0 ? _d : DefaultCartText.DELIVERY_METHOD,
        },
        recipeType: {
            display: cartSettings.DisplayRecipeType,
            label: (_e = cartSettings.RecipeTypeDisplayText) !== null && _e !== void 0 ? _e : DefaultCartText.RECIPE_TYPE,
        },
        mealSize: {
            display: cartSettings.DisplayMealSize,
            label: (_f = cartSettings.MealSizeDisplayText) !== null && _f !== void 0 ? _f : DefaultCartText.MEAL_SIZE,
        },
        subscriptionDiscount: {
            display: cartSettings.DisplaySubscriptionDiscount,
            label: (_g = cartSettings.SubscriptionDiscountTextDisplay) !== null && _g !== void 0 ? _g : DefaultCartText.SUBSCRIPTION_DISCOUNT,
        },
        location: {
            display: cartSettings.DisplayLocation,
            label: (_h = cartSettings.PickupLocationDisplayText) !== null && _h !== void 0 ? _h : DefaultCartText.PICKUP_LOCATION,
        },
        postalCode: {
            display: cartSettings.DisplayLocation,
            label: (_j = cartSettings.PostalCodeDisplayText) !== null && _j !== void 0 ? _j : DefaultCartText.POSTAL_CODE,
        },
        dayPrices: {
            display: cartSettings.DisplayDayPrices,
        },
        mealPrices: {
            display: cartSettings.DisplayMealPrices,
        },
        discountPerMeal: {
            display: cartSettings.DisplayDiscountedPricePerMeal,
        },
        products: {
            label: DefaultCartText.PRODUCT_LABEL,
        },
        recipe: {
            display: cartSettings.DisplayRecipes,
            unselectedText: cartSettings.UnselectedRecipeText,
        },
        subscriptionSection: {
            display: cartSettings.DisplaySubscriptionSection,
            label: (_k = cartSettings.SubscriptionSectionDisplayText) !== null && _k !== void 0 ? _k : DefaultCartText.SUBSCRIPTION_SECTION_LABEL,
        },
        pricing: {
            label: (_l = cartSettings.PricingTextDisplay) !== null && _l !== void 0 ? _l : DefaultCartText.PRICING,
        },
        subtotal: {
            label: (_m = cartSettings.SubtotalTextDisplay) !== null && _m !== void 0 ? _m : DefaultCartText.SUBTOTAL,
        },
        coupon: {
            label: (_o = cartSettings.CouponTextDisplay) !== null && _o !== void 0 ? _o : DefaultCartText.COUPON,
        },
        mealDiscount: {
            label: (_p = cartSettings.MealDiscountTextDisplay) !== null && _p !== void 0 ? _p : DefaultCartText.DISCOUNT,
        },
        delivery: {
            label: (_q = cartSettings.DeliveryTextDisplay) !== null && _q !== void 0 ? _q : DefaultCartText.DELIVERY,
        },
        tax: {
            label: (_r = cartSettings.TaxTextDisplay) !== null && _r !== void 0 ? _r : DefaultCartText.TAX,
        },
        total: {
            label: (_s = cartSettings.TotalTextDisplay) !== null && _s !== void 0 ? _s : DefaultCartText.TOTAL,
        },
        surcharge: {
            label: (_t = cartSettings.SurchargeTextDisplay) !== null && _t !== void 0 ? _t : DefaultCartText.SURCHARGE,
        },
        displayOfQuantitySelectedText: {
            display: (_u = cartSettings.EnableDisplayOfQuantitySelectedText) !== null && _u !== void 0 ? _u : true,
        },
        shortWeekIndicatorForMeals: {
            display: cartSettings.DisplayShortWeekIndicatorForMeals,
            label: (_v = cartSettings.ShortWeekIndicatorForMealsText) !== null && _v !== void 0 ? _v : DefaultCartText.SHORT_WEEK_INDICATOR_MEALS,
        },
        shortWeekIndicatorForProducts: {
            display: cartSettings.DisplayShortWeekIndicatorForProducts,
            label: (_w = cartSettings.ShortWeekIndicatorForProductsText) !== null && _w !== void 0 ? _w : DefaultCartText.SHORT_WEEK_INDICATOR_PRODUCTS,
        },
        nextOrderSubtotal: {
            label: (_x = cartSettings.NextOrderSubtotalDisplayText) !== null && _x !== void 0 ? _x : DefaultCartText.NEXT_ORDER_SUBTOTAL,
        },
        balanceDue: {
            label: DefaultCartText.BALANCE_DUE,
        },
        amountPaid: {
            label: DefaultCartText.AMOUNT_PAID,
        },
        accountCredit: {
            label: DefaultCartText.ACCOUNT_CREDIT,
        },
        giftCard: {
            label: DefaultCartText.GIFT_CARD,
        },
        noBalanceDueMessage: {
            display: showNoBalanceDueMessage,
            label: cartSettings.NoBalanceDueMessage,
        },
        hidePrices,
        cartSelectionMode: cartSettings.CartSelectionMode,
        recipeDisplayNameType: cartSettings.RecipeDisplayNameType,
        detailSectionHeading: cartSettings.CartDetailSectionHeading,
        productSectionHeading: cartSettings.ProductSectionHeading,
        isDayDefaultExpanded: cartSettings.DefaultDaySectionExpansion === DefaultDaySectionExpansionType.EXPANDED,
        emptyCartDisplayMessage: cartSettings.EmptyCartDisplayMessage,
        displayContinueButton: cartSettings.DisplayContinueButton,
        questions: {
            display: cartSettings.DisplayQuestionSection,
            label: cartSettings.QuestionSectionTitle,
        },
    };
};
